import { TranslateService } from '@ngx-translate/core';
import { Injectable, inject, DOCUMENT } from '@angular/core';


import { TIPS, TIPS_DEFAULT_LANG, Tip, getRandomTip } from '../functions';
import { AppLanguage } from '../enums';

interface PreloaderClosedEvent { elapsed: number; message: string }

@Injectable({ providedIn: 'root' })
export class PreloaderService {

	private i18n = inject(TranslateService)
	private document = inject(DOCUMENT)
	private defaultLang = inject(TIPS_DEFAULT_LANG)
	private tips = inject(TIPS)

	private started = new Date().getTime()
	private preClosingMessage: string;
	private closeBtn = null
	private shown = false

	constructor() {
		const loader = this.document.querySelector('#app-loader') as HTMLElement
		this.shown = loader && loader.style.display !== 'none'

		const tipText = this.document.querySelector('#al-tip-text')
		if (tipText) this.insertTip(tipText)
	}

	insertTip(tipText: Element): void {
		const queryParams = new URLSearchParams(location.search)
		const navlang = (queryParams.get('lang') || navigator?.language || (navigator as any)?.userLanguage || this.defaultLang).split('-')[0];
		const lang = Object.values(AppLanguage).includes(navlang) ? navlang : this.defaultLang;

		console.log(`[init] tips nav lang: ${queryParams.get('lang')} | ${navigator?.language} | ${(navigator as any)?.userLanguage} => ${navlang} => ${lang}`)

		const insertHTML = (tip: Tip) => {
			tipText.classList.add('alert', 'alert-info')
			tipText.innerHTML = `💡 ${tip[lang]}`
		}

		try {
			insertHTML(JSON.parse(sessionStorage.getItem('tip')))
			console.debug(`[init] use tip from last session`)

		} catch (e) {
			console.debug(`[init] cannot restore last tip (${e}), getting a new one`)

			getRandomTip(this.tips).then(tip => {
				if (!tip || !tip[lang]) {
					console.debug(`[init] no tip found`)
					return;
				}
				sessionStorage.setItem('tip', JSON.stringify(tip))
				insertHTML(tip)
			})
		}
	}

	hasBeenShown(): boolean {
		return this.shown
	}

	withPreClosingMessage(message: string): PreloaderService {
		this.preClosingMessage = message
		return this
	}

	closePreloaderWhenPreloadersFinished(min = 2_000, max = 20_000): Promise<PreloaderClosedEvent> {
		return new Promise(resolve => {
			if (!this.hasBeenShown()) {
				return resolve(this.closePreloader({ elapsed: 0, message: `not shown` }));
			}

			const closePreloaderWhenReady = () => {
				const elapsed = new Date().getTime() - this.started

				if (elapsed >= 2_000) {
					this.showCloseButton(resolve)
				}

				if (!this.someLoadersPresent() && elapsed > min)
					return resolve(this.closePreloader({ elapsed, message: 'after all loaders finished' }))

				if (elapsed > max)
					return resolve(this.closePreloader({ elapsed, message: `after ${elapsed}ms` }))

				setTimeout(closePreloaderWhenReady, 500);
			}

			closePreloaderWhenReady()
		})
	}

	private showCloseButton(resolve: CallableFunction = () => null) {
		if (this.closeBtn) return;
		this.closeBtn = this.document.querySelector('#al-footer-close')
		if (!this.closeBtn) return;
		this.closeBtn.classList.remove('d-none')
		this.closeBtn.innerHTML = this.i18n.instant('Actions.Close')
		this.closeBtn.addEventListener('click', () => resolve(this.closePreloader({
			elapsed: new Date().getTime() - this.started,
			message: 'by user'
		})))
	}

	private closePreloader(reason: PreloaderClosedEvent) {
		console.debug(`[init] preloader hiding: ${reason.message}`)

		const footer = this.document.querySelector('#al-footer') as HTMLDivElement
		const animation = this.document.querySelector('#app-animation')
		const welcome = this.document.querySelector('#app-welcome')
		const loader = this.document.querySelector('#app-loader')
		if (!loader) return;

		animation.classList.add('al-hidden')

		if (this.preClosingMessage) {
			footer.style.visibility = 'hidden'
			welcome.innerHTML = `<h1>${this.preClosingMessage}</h1>`
			loader.classList.add('al-hidden-with-message')

			setTimeout(() => sessionStorage.removeItem('tip'), 2000)
			setTimeout(() => this.removeLoader(reason), 1000)

		} else {
			this.removeLoader(reason)
		}

		return reason;
	}

	removeLoader(reason: PreloaderClosedEvent) {
		const loader = this.document.querySelector('#app-loader')
		if (!loader) return;

		setTimeout(() => {
			console.debug(`[init] preloader removed after ${new Date().getTime() - this.started}ms`)
			loader.remove()
		}, 300)

		return reason;
	}

	private someLoadersPresent() {
		return this.document.querySelector('loader-spinner .loader')
	}
}